import is from '@sindresorhus/is';

import type { GetUsersParams } from '../../fetchers/job-architecture/job-architecture-fetchers';
import type { SearchFilters } from '../types/search-types';
import type { DebugInformationItem } from './use-debug-information';

export const jobArchitectureKeys = {
    all: ['job-architecture'] as const,
    architecture: (archId: string) => [...jobArchitectureKeys.all, 'arch', archId] as const,
    architectureDetail: (archId: string) => [...jobArchitectureKeys.all, 'architecture-detail', archId] as const,
    architectures: () => [...jobArchitectureKeys.all, 'architectures'] as const,
    architecturesWithStatistics: () => [...jobArchitectureKeys.architectures(), 'with-statistics'] as const,

    archivedLevels: (archId: string) => [...jobArchitectureKeys.architecture(archId), 'archived-levels'] as const,
    archivedRoles: (archId: string) => [...jobArchitectureKeys.architecture(archId), 'archived-roles'] as const,
    archLevelingFramework: (archId: string, frameworkId: string) =>
        [...jobArchitectureKeys.archLevelingFrameworks(archId), frameworkId] as const,
    archLevelingFrameworks: (archId: string) =>
        [...jobArchitectureKeys.architecture(archId), 'leveling-frameworks'] as const,
    assignableLevelReviewers: (archId: string, levelId: string, searchTerm: string) =>
        [...jobArchitectureKeys.level(archId, levelId), 'assignable-reviewers', searchTerm] as const,
    assignableRoleReviewers: (archId: string, roleId: string, searchTerm: string) =>
        [...jobArchitectureKeys.role(archId, roleId), 'assignable-reviewers', searchTerm] as const,
    automation: (archId: string) => [...jobArchitectureKeys.architecture(archId), 'automation'] as const,
    automationDetail: (archId: string, itemId: string, language: string) =>
        [...jobArchitectureKeys.automation(archId), 'detail', itemId, language] as const,
    automationHandover: (archId: string, layer: string, parentId: string | undefined, language: string) =>
        [...jobArchitectureKeys.automation(archId), 'handover', layer, parentId ?? '', language] as const,
    automationPoints: (archId: string, layer: string, parentId: string | undefined, language: string) =>
        [...jobArchitectureKeys.automation(archId), 'points', layer, parentId ?? '', language] as const,
    automationRanking: (archId: string, layer: string, sortBy: string, limit: number, language: string) =>
        [...jobArchitectureKeys.automation(archId), 'ranking', layer, sortBy, limit, language] as const,
    automationSearch: (archId: string, layer: string, query: string, language: string) =>
        [...jobArchitectureKeys.automation(archId), 'search', layer, query, language] as const,
    careerTracks: (archId: string) => [...jobArchitectureKeys.architecture(archId), 'career-tracks'] as const,
    changeProposalCompare: (blockId: string) =>
        [...jobArchitectureKeys.all, 'change-proposal-compare', blockId] as const,
    clusters: (archId: string) => [...jobArchitectureKeys.architecture(archId), 'clusters'] as const,
    companyInformationGeneration: (archId: string) =>
        [...jobArchitectureKeys.architecture(archId), 'company-information-generation'] as const,
    clustersByFamily: (archId: string, familyId: string) =>
        [...jobArchitectureKeys.clusters(archId), 'by-family', familyId] as const,
    commentStatistics: (archId: string, language: string) =>
        [...jobArchitectureKeys.architecture(archId), 'comment-statistics', language] as const,
    competencies: () => [...jobArchitectureKeys.all, 'competencies'] as const,
    debugInfo: (archId: string, item: DebugInformationItem) =>
        [...jobArchitectureKeys.architecture(archId), 'debug-info', item.type, item.id] as const,
    families: (archId: string) => [...jobArchitectureKeys.architecture(archId), 'families'] as const,
    familiesWithClusters: (archId: string) => [...jobArchitectureKeys.families(archId), 'with-clusters'] as const,
    familiesRegenerationResults: (archId: string | undefined) =>
        archId === undefined
            ? ([...jobArchitectureKeys.all, 'families-regeneration-results'] as const)
            : ([...jobArchitectureKeys.architecture(archId), 'families-regeneration-results'] as const),
    family: (archId: string, familyId: string) => [...jobArchitectureKeys.families(archId), familyId] as const,
    filters: (archId: string) => [...jobArchitectureKeys.architecture(archId), 'filters'] as const,
    history: (archId: string) => [...jobArchitectureKeys.architecture(archId), 'history'] as const,
    inflight: (archId: string) => [...jobArchitectureKeys.architecture(archId), 'inflight'] as const,
    itemPath: (archId: string, itemId: string) =>
        [...jobArchitectureKeys.architecture(archId), 'item-path', itemId] as const,
    jobProfileManagement: () => [...jobArchitectureKeys.all, 'job-profile-management'] as const,
    jobProfileManagementProfiles: (
        architectureId: string,
        searchTerm: string,
        reviewerId: string | undefined,
        reviewerAssignment: string | undefined,
        type: readonly string[],
        sort: { readonly direction: 'asc' | 'desc'; readonly field: string } | undefined,
    ) =>
        [
            ...jobArchitectureKeys.jobProfileManagement(),
            architectureId,
            searchTerm,
            reviewerId ?? '',
            reviewerAssignment ?? '',
            [...type].join(','),
            sort?.field ?? '',
            sort?.direction ?? '',
        ] as const,
    level: (archId: string, levelId: string, version?: string, includeComments?: boolean) => {
        const commentsDiscriminator = includeComments === true ? 'with-comments' : 'no-comments';
        if (is.undefined(version)) {
            return [...jobArchitectureKeys.levelItem(archId, levelId), commentsDiscriminator] as const;
        }

        return [...jobArchitectureKeys.levelItem(archId, levelId), version, commentsDiscriminator] as const;
    },
    /** Only the current version of a level, kept apart from `level` so its details cache keeps its own shape. */
    levelCurrentVersion: (archId: string, levelId: string) =>
        [...jobArchitectureKeys.levelItem(archId, levelId), 'current-version'] as const,
    levelItem: (archId: string, levelId: string) => [...jobArchitectureKeys.levels(archId), levelId] as const,
    levelingFramework: (id: string | null) => [...jobArchitectureKeys.levelingFrameworks(), id] as const,
    levelingFrameworks: () => [...jobArchitectureKeys.all, 'leveling-frameworks'] as const,
    levels: (archId: string) => [...jobArchitectureKeys.architecture(archId), 'levels'] as const,
    levelsByRole: (archId: string, roleId: string) =>
        [...jobArchitectureKeys.levels(archId), 'by-role', roleId] as const,
    levelVersions: (archId: string, levelId: string) =>
        [...jobArchitectureKeys.level(archId, levelId), 'versions'] as const,
    metadata: (archId: string) => [...jobArchitectureKeys.architecture(archId), 'metadata'] as const,
    onboardingState: (archId: string | null) =>
        archId === null
            ? ([...jobArchitectureKeys.all, 'onboarding-state'] as const)
            : ([...jobArchitectureKeys.architecture(archId), 'onboarding-state'] as const),
    options: () => [...jobArchitectureKeys.all, 'options'] as const,
    regenerationResult: (archId: string, regenerationId: string) =>
        [...jobArchitectureKeys.architecture(archId), 'regeneration-result', regenerationId] as const,
    profile: (archId: string, profileId: string) => [...jobArchitectureKeys.profiles(archId), profileId] as const,
    profiles: (archId: string) => [...jobArchitectureKeys.architecture(archId), 'profiles'] as const,
    relatedSkills: (skills: readonly { readonly id: string }[], acceptLanguage: string) =>
        [
            ...jobArchitectureKeys.all,
            'skills',
            'related',
            skills
                .map((s) => s.id)
                .sort()
                .join(','),
            acceptLanguage,
        ] as const,
    role: (archId: string, roleId: string, version?: string, includeComments?: boolean) => {
        const commentsDiscriminator = includeComments === true ? 'with-comments' : 'no-comments';
        if (is.undefined(version)) {
            return [...jobArchitectureKeys.roleItem(archId, roleId), commentsDiscriminator] as const;
        }

        return [...jobArchitectureKeys.roleItem(archId, roleId), version, commentsDiscriminator] as const;
    },
    roleItem: (archId: string, roleId: string) => [...jobArchitectureKeys.roles(archId), roleId] as const,
    roles: (archId: string) => [...jobArchitectureKeys.architecture(archId), 'roles'] as const,
    rolesAndLevels: (archId: string) => [...jobArchitectureKeys.architecture(archId), 'roles-and-levels'] as const,
    rolesAndLevelsByCluster: (archId: string, clusterId: string) =>
        [...jobArchitectureKeys.rolesAndLevels(archId), 'by-cluster', clusterId] as const,
    rolesByCluster: (archId: string, clusterId: string) =>
        [...jobArchitectureKeys.roles(archId), 'by-cluster', clusterId] as const,
    roleVersions: (archId: string, roleId: string) =>
        [...jobArchitectureKeys.role(archId, roleId), 'versions'] as const,
    roleComparisonSearch: (
        archId: string,
        itemType: 'job-level' | 'job-role',
        searchTerm: string,
        acceptLanguage: string,
    ) =>
        [
            ...jobArchitectureKeys.architecture(archId),
            'role-comparison-search',
            itemType,
            searchTerm,
            acceptLanguage,
        ] as const,
    search: (archId: string, filters: SearchFilters) =>
        [...jobArchitectureKeys.architecture(archId), 'search', filters] as const,
    skillCatalog: () => ['skill-catalog'] as const,
    skillCatalogOptions: () => ['skill-catalog', 'options'] as const,
    skillCatalogStatistics: (filters?: {
        readonly jobArchitectureIds?: readonly string[];
        readonly levelIds?: readonly string[];
        readonly roleIds?: readonly string[];
        readonly searchTerm?: string;
        readonly skillTypes?: readonly string[];
        readonly sources?: readonly string[];
        readonly statusFilter?: readonly string[];
    }) => ['skill-catalog', 'statistics', filters] as const,
    skillDetails: (skillId: string, language: string) => ['skill-details', skillId, language] as const,
    skillLimits: (archId: string) => [...jobArchitectureKeys.architecture(archId), 'skill-limits'] as const,
    skillLocales: () => [...jobArchitectureKeys.all, 'skill-locales'] as const,
    skillsBySearchTerm: (searchTerm: string, acceptLanguage: string, sort?: string) =>
        [...jobArchitectureKeys.all, 'skills', 'search', searchTerm, acceptLanguage, sort] as const,
    skillsCatalogSearch: (
        searchTerm: string,
        acceptLanguage: string,
        sort?: string,
        statusFilter?: readonly string[],
        roleIds?: readonly string[],
        levelIds?: readonly string[],
        skillTypes?: readonly string[],
        sources?: readonly string[],
        jobArchitectureIds?: readonly string[],
    ) =>
        [
            ...jobArchitectureKeys.skillCatalog(),
            'search',
            searchTerm,
            acceptLanguage,
            sort,
            statusFilter,
            roleIds,
            levelIds,
            skillTypes,
            sources,
            jobArchitectureIds,
        ] as const,
    taskManagement: () => [...jobArchitectureKeys.all, 'task-management'] as const,
    taskManagementClusters: (architectureId: string) =>
        [...jobArchitectureKeys.taskManagement(), 'clusters', architectureId] as const,
    taskManagementTask: (taskId: string, version: number | undefined, language?: string) =>
        [
            ...jobArchitectureKeys.taskManagement(),
            'task',
            taskId,
            version ?? 'latest',
            ...(language === undefined ? [] : [language]),
        ] as const,
    workTaskAutomationRecommendation: (taskId: string) =>
        [...jobArchitectureKeys.taskManagement(), 'automation-recommendation', taskId] as const,
    taskManagementTaskSearch: (architectureId: string, searchTerm: string) =>
        [...jobArchitectureKeys.taskManagement(), 'task-search', architectureId, searchTerm] as const,
    taskManagementClusterTasks: (architectureId: string, clusterId: string | null) =>
        [...jobArchitectureKeys.taskManagement(), 'cluster-tasks', architectureId, clusterId] as const,
    users: (params: GetUsersParams) => [...jobArchitectureKeys.all, 'users', params] as const,
    watches: () => [...jobArchitectureKeys.all, 'watches'] as const,
} as const;
